import { useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
    Settings as SettingsIcon,
    CalendarDays,
    Download,
    Upload,
    AlertTriangle,
    Save,
    Music2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { storage } from "../lib/storage";
import { DAYS_OF_WEEK } from "../lib/types";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";

/**
 * Studio Settings — admin-only. Lets the studio owner configure:
 *   • Studio name (header label)
 *   • Monthly target multiplier (per-student target = lesson days × this)
 *   • Working days (which days lessons actually happen)
 *   • Backup (export JSON) / Restore (import JSON)
 *   • Reset studio (wipe everything → first-launch setup)
 */
export default function Settings() {
    const { isAdmin } = useAuth();
    const { settings, updateSettings } = useSettings();

    const [studioName, setStudioName] = useState(settings.studioName);
    const [multiplier, setMultiplier] = useState(
        String(settings.monthlyTargetMultiplier),
    );
    const [workingDays, setWorkingDays] = useState(settings.workingDays);
    const fileInputRef = useRef(null);

    if (!isAdmin) return <Navigate to="/" replace />;

    const toggleDay = (d) =>
        setWorkingDays((prev) =>
            prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
        );

    const isDirty =
        studioName.trim() !== settings.studioName ||
        Number(multiplier) !== settings.monthlyTargetMultiplier ||
        workingDays.length !== settings.workingDays.length ||
        workingDays.some((d) => !settings.workingDays.includes(d));

    const saveGeneral = () => {
        const mult = Number(multiplier);
        if (!Number.isFinite(mult) || mult < 1 || mult > 6) {
            toast.error("Monthly multiplier must be a whole number between 1 and 6.");
            return;
        }
        if (workingDays.length === 0) {
            toast.error("Pick at least one working day.");
            return;
        }
        updateSettings({
            studioName: studioName.trim(),
            monthlyTargetMultiplier: Math.round(mult),
            workingDays,
        });
        toast.success("Studio settings saved");
    };

    const exportData = () => {
        try {
            const snapshot = storage.exportAll();
            const blob = new Blob([JSON.stringify(snapshot, null, 2)], {
                type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
            a.href = url;
            a.download = `tempo-backup-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
        } catch {
            toast.error("Could not export. Try again.");
        }
    };

    const triggerImport = () => fileInputRef.current?.click();

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (
            !window.confirm(
                "Importing will REPLACE all current studio data. Continue?",
            )
        ) {
            return;
        }
        try {
            const text = await file.text();
            const payload = JSON.parse(text);
            storage.importAll(payload);
            toast.success("Backup restored. Reloading…");
            setTimeout(() => window.location.reload(), 700);
        } catch (err) {
            toast.error(err.message || "Could not read backup file.");
        }
    };

    const resetStudio = () => {
        storage.wipeAll();
        toast.success("Studio reset. Starting fresh…");
        setTimeout(() => {
            window.location.href = "/setup";
        }, 500);
    };

    return (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
            <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-1">
                    <SettingsIcon size={11} strokeWidth={3} />
                    Admin
                </div>
                <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight">
                    Studio Settings
                </h1>
                <p className="text-slate-500 mt-2 max-w-xl">
                    Configure how your studio runs. Changes apply across every teacher
                    workspace.
                </p>
            </div>

            {/* General */}
            <section
                className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8 space-y-6"
                data-testid="settings-general"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-mist text-slate-600 flex items-center justify-center">
                        <Music2 size={18} strokeWidth={2.6} />
                    </div>
                    <h2 className="font-display font-bold text-2xl uppercase tracking-tight">
                        General
                    </h2>
                </div>

                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Studio Name
                    </Label>
                    <Input
                        value={studioName}
                        onChange={(e) => setStudioName(e.target.value)}
                        placeholder="e.g. Tempo (or your studio's name)"
                        maxLength={40}
                        className="h-12 rounded-xl bg-mist/50 border-mist focus-visible:ring-[#3D6385]"
                        data-testid="settings-studio-name"
                    />
                    <p className="text-xs text-slate-500">
                        Shown in the header. Leave empty to display{" "}
                        <span className="font-bold">Tempo</span>.
                    </p>
                </div>

                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Monthly Target Multiplier
                    </Label>
                    <div className="flex items-center gap-3">
                        <Input
                            type="number"
                            min={1}
                            max={6}
                            step={1}
                            value={multiplier}
                            onChange={(e) => setMultiplier(e.target.value)}
                            className="h-12 w-28 rounded-xl bg-mist/50 border-mist focus-visible:ring-[#3D6385] text-center font-display font-bold text-lg"
                            data-testid="settings-multiplier"
                        />
                        <div className="text-sm text-slate-500">
                            Per-student target ={" "}
                            <span className="font-bold text-slate-700">lesson days × {multiplier || 1}</span>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500">
                        Default 4 (one lesson per scheduled day, per week). Set higher for
                        denser months.
                    </p>
                </div>
            </section>

            {/* Working week */}
            <section
                className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8 space-y-5"
                data-testid="settings-working-week"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-soft text-[#4A6F8F] flex items-center justify-center">
                        <CalendarDays size={18} strokeWidth={2.6} />
                    </div>
                    <div>
                        <h2 className="font-display font-bold text-2xl uppercase tracking-tight">
                            Working Week
                        </h2>
                        <p className="text-sm text-slate-500">
                            Days your studio runs lessons. Affects the Add Student form.
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((d) => {
                        const active = workingDays.includes(d);
                        return (
                            <button
                                key={d}
                                type="button"
                                onClick={() => toggleDay(d)}
                                data-testid={`settings-day-${d.toLowerCase()}`}
                                aria-pressed={active}
                                className={`focus-ring px-4 h-10 rounded-full text-sm font-bold transition-all ${
                                    active
                                        ? "bg-[#3D6385] text-white shadow-md"
                                        : "bg-mist/50 text-slate-600 hover:bg-mist"
                                }`}
                            >
                                {d.slice(0, 3)}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Save bar */}
            <div className="sticky bottom-4 z-10 flex justify-end">
                <Button
                    onClick={saveGeneral}
                    disabled={!isDirty}
                    className="focus-ring rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-12 px-6 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="settings-save-btn"
                >
                    <Save size={16} strokeWidth={2.8} className="mr-2" />
                    Save Settings
                </Button>
            </div>

            {/* Backup */}
            <section
                className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8 space-y-5"
                data-testid="settings-backup"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-success-soft text-success flex items-center justify-center">
                        <Download size={18} strokeWidth={2.6} />
                    </div>
                    <div>
                        <h2 className="font-display font-bold text-2xl uppercase tracking-tight">
                            Backup &amp; Restore
                        </h2>
                        <p className="text-sm text-slate-500">
                            Studio data lives on this device. Export it as JSON for a safe
                            copy, or restore from a previous backup.
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-3">
                    <Button
                        onClick={exportData}
                        variant="outline"
                        className="focus-ring rounded-xl h-11 px-5 font-bold border-mist hover:bg-mist/50"
                        data-testid="settings-export-btn"
                    >
                        <Download size={16} strokeWidth={2.8} className="mr-2" />
                        Export backup (JSON)
                    </Button>
                    <Button
                        onClick={triggerImport}
                        variant="outline"
                        className="focus-ring rounded-xl h-11 px-5 font-bold border-mist hover:bg-mist/50"
                        data-testid="settings-import-btn"
                    >
                        <Upload size={16} strokeWidth={2.8} className="mr-2" />
                        Restore from file
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/json,.json"
                        onChange={handleImport}
                        className="hidden"
                        data-testid="settings-import-input"
                    />
                </div>
            </section>

            {/* Danger Zone */}
            <section
                className="bg-danger-soft/60 rounded-3xl border border-danger-soft p-6 sm:p-8 space-y-5"
                data-testid="settings-danger"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-danger-soft text-danger flex items-center justify-center">
                        <AlertTriangle size={18} strokeWidth={2.6} />
                    </div>
                    <div>
                        <h2 className="font-display font-bold text-2xl uppercase tracking-tight text-danger">
                            Danger Zone
                        </h2>
                        <p className="text-sm text-danger/70">
                            Wipe all studio data and start over. This cannot be undone.
                        </p>
                    </div>
                </div>

                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button
                            variant="outline"
                            className="focus-ring rounded-xl h-11 px-5 font-bold text-danger border-danger/30 hover:bg-danger-soft bg-white"
                            data-testid="settings-reset-btn"
                        >
                            <AlertTriangle
                                size={16}
                                strokeWidth={2.8}
                                className="mr-2"
                            />
                            Reset studio
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-3xl">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="font-display uppercase tracking-tight">
                                Reset entire studio?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                This permanently deletes all teachers, students, attendance
                                records, and settings. You&apos;ll be returned to the first-launch
                                setup screen.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel
                                className="rounded-xl font-bold"
                                data-testid="settings-reset-cancel"
                            >
                                Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={resetStudio}
                                className="rounded-xl bg-danger hover:bg-danger/85 font-bold"
                                data-testid="settings-reset-confirm"
                            >
                                Yes, reset everything
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </section>
        </div>
    );
}
